import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { CHALLAN_ITEM_STATUSES } from '@/constants/values'
import { USER_ROLE_KEYS } from '@/constants/roles'
import { getUserRoles } from '@/lib/auth'
import { useCallback, useRef } from 'react'

// ---------------------------------------------------------------------------
// Toast helpers (replaces @shared/utils showToastMessage)
// ---------------------------------------------------------------------------

/**
 * Success: role="status", auto-dismisses after 5s (paused on hover/focus).
 * Error: role="alert", stays until dismissed so the failed action is never missed.
 * The third argument only changes the success duration.
 */
export const showToastMessage = (
  message: string,
  isSuccess = true,
  autoClose: number | false = 5000,
): void => {
  if (isSuccess) {
    toast.success(message, { autoClose, role: 'status', pauseOnHover: true, pauseOnFocusLoss: true })
  } else {
    toast.error(message, { autoClose: false, role: 'alert' })
  }
}

export const showToastWarning = (message: string): void => {
  toast.warning(message, { autoClose: 8000, role: 'status', pauseOnHover: true, pauseOnFocusLoss: true })
}

export const showToastInfo = (message: string): void => {
  toast.info(message, { autoClose: 5000, role: 'status', pauseOnHover: true, pauseOnFocusLoss: true })
}

// ---------------------------------------------------------------------------
// Date formatters
// ---------------------------------------------------------------------------

export const formatDate = (date: Date | null | undefined): string => {
  if (!date) return ''
  const year = date.toLocaleString('default', { year: 'numeric' })
  const month = date.toLocaleString('default', { month: '2-digit' })
  const day = date.toLocaleString('default', { day: '2-digit' })
  return `${year}-${month}-${day}`
}

export const formatDateV2 = (date: string | null | undefined): string => {
  if (!date) return ''
  return date.substring(0, 10)
}

export const formatDateTime = (dateTime: string | null | undefined): string => {
  if (!dateTime) return ''
  return dayjs(dateTime).format('YYYY-MM-DD hh:mm A')
}

export const formatDateToYYYYMMDD = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export const formatDateToTime = (date: string | Date): string => {
  const jsDate = new Date(date)
  const istDate = new Date(jsDate.getTime() + (5 * 60 + 30) * 60 * 1000)
  return istDate.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  })
}

export const formatTime12Hour = (timeString: string | null | undefined): string => {
  if (!timeString) return '--'
  const [hours, minutes] = timeString.split(':')
  const hour = parseInt(hours, 10)
  const minute = parseInt(minutes, 10)
  const ampm = hour >= 12 ? 'PM' : 'AM'
  let hour12 = hour % 12
  hour12 = hour12 ? hour12 : 12
  const minuteStr = minute < 10 ? `0${minute}` : String(minute)
  return `${hour12}:${minuteStr} ${ampm}`
}

// ---------------------------------------------------------------------------
// String utilities
// ---------------------------------------------------------------------------

export const capitalizeFirstLetter = (str: string): string => {
  if (!str) return ''
  return str
    .replace(/_/g, ' ')
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

// ---------------------------------------------------------------------------
// Status display helpers
// ---------------------------------------------------------------------------

export const getChallanItemStatus = (
  name: string,
): { css: string; label: string } | undefined => {
  const map: Record<string, { css: string; label: string }> = {
    ASSIGNED: { css: 'status-assigned', label: 'Assigned' },
    UNASSIGNED: { css: 'status-unassigned', label: 'Unassigned' },
    CREATED: { css: 'status-unassigned', label: 'Unassigned' },
    PAID: { css: 'dispose-paid', label: 'Paid' },
    PAID_RECEIPT_PENDING: { css: 'status-pending', label: 'Paid Receipt Pending' },
    REATTEMPT: { css: 'status-pending', label: 'Reattempt' },
    FULFILLED: { css: 'status-resolved', label: 'Fulfilled' },
    SUBMITTED: { css: 'status-unassigned', label: 'Unassigned' },
    AGENT_ASSIGNED: { css: 'dispose-agent-assigned', label: 'Agent Assigned' },
    UNABLE_TO_FULFILL: { css: 'unable-fullfil', label: 'Unable to Fulfill' },
    VIRTUAL_COURT_PROCEEDING: { css: 'dispose-vc-proceeding', label: 'VC Proceeding' },
    VC_PROCEEDING: { css: 'dispose-vc-proceeding', label: 'VC Proceeding' },
    RAISE_ACCEPTED: { css: 'status-raise-accepted', label: 'Raise Accepted' },
    ACCEPTED: { css: 'status-raise-accepted', label: 'Raise Accepted' },
    RAISE_REJECTED: { css: 'status-raise-rejected', label: 'Raise Rejected' },
    PAYMENT_RAISED: { css: 'status-raised', label: 'Payment Raised' },
    RAISE_AMENDED: { css: 'status-amended', label: 'Raise Amended' },
    AMENDED: { css: 'status-amended', label: 'Raise Amended' },
    REJECTED: { css: 'unable-fullfil', label: 'Rejected' },
    DELAYED: { css: 'status-pending', label: 'Delayed' },
    PAID_BUT_PENDING: { css: 'status-pending', label: 'Paid But Pending' },
    PENDING: { css: 'status-pending', label: 'Pending' },
    UPLOADED: { css: 'status-pending', label: 'Uploaded' },
    SENT_FOR_PDF_TO_JSON_PROCESSING: { css: 'status-pending', label: 'Processing PDF' },
    PDF_TO_JSON_PROCESSING_FAILED: { css: 'status-raise-rejected', label: 'PDF processing failed' },
    PDF_TO_JSON_PROCESSING_SUCCESS: { css: 'status-assigned', label: 'PDF processing success' },
    SENT_FOR_AUTOMATED_PDF_GENERATION: { css: 'status-pending', label: 'Generating PDF' },
    AUTOMATED_PDF_GENERATION_SUCCESS: { css: 'status-assigned', label: 'PDF generation success' },
    AUTOMATED_PDF_GENERATION_FAILED: { css: 'status-raise-rejected', label: 'PDF generation failed' },
  }
  return map[name]
}

// getStatus is used for non-item operational records — different from getChallanItemStatus.
// Matches panels-operations/src/utils/index.js getStatus()
export const getStatus = (name: string): { css: string; label: string } | undefined => {
  const map: Record<string, { css: string; label: string }> = {
    ASSIGNED: { css: 'status-assigned', label: 'Assigned' },
    RESOLVED: { css: 'status-resolved', label: 'Resolved' },
    FULFILLED: { css: 'status-resolved', label: 'Fulfilled' },
    REOPENED: { css: 'unable-fullfil', label: 'Reopened' },
    UNABLE_TO_FULFILL: { css: 'unable-fullfil', label: 'Unable to fulfill' },
    CREATED: { css: 'status-unassigned', label: 'Unassigned' },
    REJECTED: { css: 'status-rejected', label: 'Rejected' },
    UNASSIGNED: { css: 'status-unassigned', label: 'Unassigned' },
    RAISE_ACCEPTED: { css: 'status-raise-accepted', label: 'Raise Accepted' },
    ACCEPTED: { css: 'status-raise-accepted', label: 'Accepted' },
    RAISE_REJECTED: { css: 'status-raise-rejected', label: 'Raise Rejected' },
    PAYMENT_RAISED: { css: 'status-raised', label: 'Payment Raised' },
    PAID_RECEIPT_PENDING: { css: 'status-pending', label: 'Paid Receipt Pending' },
    AMENDED: { css: 'status-amended', label: 'Raise Amended' },
    PENDING: { css: 'status-pending', label: 'PENDING' },
    UPLOADED: { css: 'status-pending', label: 'Uploaded' },
    SENT_FOR_PDF_TO_JSON_PROCESSING: { css: 'status-pending', label: 'Processing PDF' },
    PDF_TO_JSON_PROCESSING_FAILED: { css: 'status-raise-rejected', label: 'PDF processing failed' },
    PDF_TO_JSON_PROCESSING_SUCCESS: { css: 'status-assigned', label: 'PDF processing success' },
    SENT_FOR_AUTOMATED_PDF_GENERATION: { css: 'status-pending', label: 'Generating PDF' },
    AUTOMATED_PDF_GENERATION_SUCCESS: { css: 'status-assigned', label: 'PDF generation success' },
    AUTOMATED_PDF_GENERATION_FAILED: { css: 'status-raise-rejected', label: 'PDF generation failed' },
  }
  return map[name]
}

export const getPossibleTransitions = (
  transition: string,
): { value: string; label: string } | undefined => {
  const map: Record<string, string> = {
    ASSIGNED: 'Assigned',
    FULFILLED: 'Fulfilled',
    UNABLE_TO_FULFILL: 'Unable to fulfill',
    UNASSIGNED: 'Unassigned',
    UPLOADED: 'Uploaded',
    SENT_FOR_PDF_TO_JSON_PROCESSING: 'Pdf Processing',
    PDF_TO_JSON_PROCESSING_FAILED: 'Pdf Processing Failed',
    PDF_TO_JSON_PROCESSING_SUCCESS: 'Pdf Processing Success',
    SENT_FOR_AUTOMATED_PDF_GENERATION: 'Generating Automated Pdf',
    AUTOMATED_PDF_GENERATION_SUCCESS: 'Automated pdf generated',
    AUTOMATED_PDF_GENERATION_FAILED: 'Automated pdf failed',
  }
  const label = map[transition]
  return label ? { value: transition, label } : undefined
}

export const getPdfType = (type: string): string => {
  const map: Record<string, string> = {
    RAW_REPORT: 'Raw Report',
    REPORT_WITH_SUMMARY: 'Report With Summary',
  }
  return map[type] ?? type
}

// ---------------------------------------------------------------------------
// Role / user type resolution
// ---------------------------------------------------------------------------

export const getFFUserType = (): string => {
  const userAccessRoles = getUserRoles()

  if (userAccessRoles.includes(USER_ROLE_KEYS.ADMIN)) {
    return USER_ROLE_KEYS.ADMIN
  }
  if (userAccessRoles.includes(USER_ROLE_KEYS.MANAGER)) {
    return USER_ROLE_KEYS.MANAGER
  }
  if (userAccessRoles.includes(USER_ROLE_KEYS.DISPOSER)) {
    return USER_ROLE_KEYS.DISPOSER
  }
  if (userAccessRoles.includes(USER_ROLE_KEYS.AGENT)) {
    return USER_ROLE_KEYS.AGENT
  }
  if (userAccessRoles.includes(USER_ROLE_KEYS.VIEWER)) {
    return USER_ROLE_KEYS.VIEWER
  }

  return userAccessRoles[0] ?? ''
}

// ---------------------------------------------------------------------------
// Query string / object utilities (replaces @shared/utils)
// ---------------------------------------------------------------------------

export const convertObjectToParams = (params: Record<string, unknown>): string => {
  const parts = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
  return parts.length ? `?${parts.join('&')}` : ''
}

export const isObjectEmpty = (obj: Record<string, unknown>): boolean =>
  !obj || Object.keys(obj).length === 0

export const getUserFirstNameFromEmail = (email: string): string => {
  if (!email) return ''
  const local = email.split('@')[0] ?? ''
  const firstName = local.split('.')[0] ?? local
  return firstName.charAt(0).toUpperCase() + firstName.slice(1)
}

export const getArrayItemValueByLabel = (
  data: Array<{ label: string; value: unknown }> | undefined,
  label: string,
): unknown => {
  if (!data) return label
  const found = data.find((item) => item.label === label)
  return found ? found.value : label
}

export const shortenString = (str: string | undefined | null, limit: number): string => {
  if (!str) return ''
  return str.length > limit ? `${str.slice(0, limit)}...` : str
}

// ---------------------------------------------------------------------------
// Debounce hook
// ---------------------------------------------------------------------------

export const useDebounce = <T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number,
): ((...args: Parameters<T>) => void) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  return useCallback(
    (...args: Parameters<T>) => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      timeoutRef.current = setTimeout(() => {
        func(...args)
      }, wait)
    },
    [func, wait],
  )
}
